import {
  AES_GCM_IV_BYTES,
  AES_GCM_PARAMS,
  PBKDF2_ITERATIONS,
  PBKDF2_SALT_BYTES,
} from './constants';
import { arrayBufferToBase64, base64ToArrayBuffer, getWebCrypto } from './encoding';
import { randomBytes } from './random';
import type { AesGcmCiphertext } from './types';

export async function deriveKeyFromVaultSecret(
  vaultSecret: string,
  salt: string,
): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const secretMaterial = await cryptoApi.subtle.importKey(
    'raw',
    base64ToArrayBuffer(vaultSecret),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return cryptoApi.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: base64ToArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    secretMaterial,
    AES_GCM_PARAMS,
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function deriveKeyFromRecoveryKey(
  recoveryKey: string,
  salt: string,
): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const secretMaterial = await cryptoApi.subtle.importKey(
    'raw',
    base64ToArrayBuffer(recoveryKey),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return cryptoApi.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: base64ToArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    secretMaterial,
    AES_GCM_PARAMS,
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function aesGcmEncrypt(
  plaintext: BufferSource,
  key: CryptoKey,
  iv?: Uint8Array,
): Promise<AesGcmCiphertext> {
  const { ciphertext, iv: ivBase64 } = await aesGcmEncryptBuffer(plaintext, key, iv);

  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    iv: ivBase64,
  };
}

/** Encrypt to raw bytes — avoids a base64 round-trip for large uploads. */
export async function aesGcmEncryptBuffer(
  plaintext: BufferSource,
  key: CryptoKey,
  iv?: Uint8Array,
): Promise<{ ciphertext: ArrayBuffer; iv: string }> {
  const cryptoApi = getWebCrypto();
  const initializationVector = iv ?? randomBytes(AES_GCM_IV_BYTES);
  const ciphertext = await cryptoApi.subtle.encrypt(
    { name: 'AES-GCM', iv: initializationVector as Uint8Array<ArrayBuffer> },
    key,
    plaintext,
  );

  return {
    ciphertext,
    iv: arrayBufferToBase64(initializationVector.buffer as ArrayBuffer),
  };
}

export async function aesGcmDecrypt(
  ciphertextBase64: string,
  key: CryptoKey,
  ivBase64: string,
): Promise<ArrayBuffer> {
  return aesGcmDecryptBuffer(base64ToArrayBuffer(ciphertextBase64), key, ivBase64);
}

/** Decrypt raw ciphertext bytes — avoids a base64 round-trip for large previews. */
export async function aesGcmDecryptBuffer(
  ciphertext: BufferSource,
  key: CryptoKey,
  ivBase64: string,
): Promise<ArrayBuffer> {
  const cryptoApi = getWebCrypto();

  return cryptoApi.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToArrayBuffer(ivBase64) },
    key,
    ciphertext,
  );
}

export function generateSalt(): string {
  return arrayBufferToBase64(randomBytes(PBKDF2_SALT_BYTES).buffer as ArrayBuffer);
}
